import {useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useImages} from "contexts/images";
import {useSetting} from "contexts/setting";
import {useSceneScope} from "hooks/useSceneScope";
import {knownTemplatesFor} from "data/templates";
import {coverFitTransform} from "libs/utils";
import {getTemplateInstanceSize} from "data/templateManifest";

export function usePointCloudBackground(ready: boolean, derived: boolean) {
  const [params] = useSearchParams();
  const id = params.get("privateBackground"), background = params.get("background");
  const images: any = useImages(), setting: any = useSetting(), scope = useSceneScope();
  const latest = useRef({images, scope}); latest.current = {images, scope};
  const [error, setError] = useState("");
  const applied = useRef("");
  const {dataChannel, dataType, product, locked, editBgOption} = setting;
  const key = [id, dataChannel, product?.value, dataType,
    params.get("set"), params.get("layout"), params.get("variant")].join(":");
  useEffect(() => {
    if (!id || background !== "custom") { applied.current = ""; setError(""); return; }
    if (product?.value === "ALL") {
      applied.current = "";
      setError("Choose a specific product to use a Point Cloud background. ALL-products has separate background selections.");
      return;
    }
    // A changed default can replace the shared image on the same route.
    // Wait for its apply effect, then apply the import to that new default.
    if (!ready) { applied.current = ""; return; }
    if (applied.current === key) return;
    let stopped = false;
    fetch(`/api/point-cloud/backgrounds/${encodeURIComponent(id)}`, {credentials: "same-origin", cache: "no-store"}).then(async response => {
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Could not load your imported background");
      if (!result.url || !result.width || !result.height) throw new Error("Invalid imported background response");
      if (stopped) return;
      const current = latest.current.images;
      const x: any = {}, y: any = {}, scale: any = {}, rotate: any = {}, flip: any = {};
      const templateIds: string[] = knownTemplatesFor(dataChannel, product.value).map((template: any) => template.id);
      const sharedIds = templateIds.filter(templateId =>
        !locked.includes(templateId) && latest.current.scope.modeOf(templateId, "image") !== "own");
      latest.current.scope.yieldPresets("image", sharedIds);
      for (const templateId of sharedIds) {
        const {width: w, height: h} = getTemplateInstanceSize(dataChannel, templateId, product.value);
        const fit = coverFitTransform(result.width, result.height, w, h);
        x[templateId] = fit.x; y[templateId] = fit.y; scale[templateId] = fit.scale;
        rotate[templateId] = 0; flip[templateId] = {x: false, y: false};
        if (derived) current.setUserBgAdjustment("custom", templateId, {...fit, rotate: 0, flip: {x: false, y: false}});
      }
      if (dataChannel === "ads" || dataChannel === "livestream") {
        current.editCustomBgs("main", result.url, false);
        // Livestream renders per-template slots over its bundled defaults.
        if (dataChannel === "livestream") for (const templateId of sharedIds) {
          current.editCustomBgs(templateId, result.url, true);
          current.editCustomBgUploaded(templateId, true);
        }
      }
      else current.editCustomBg(result.url);
      if (!derived) current.editTransform({...current.bgX, ...x}, {...current.bgY, ...y}, {...current.bgScale, ...scale}, {...current.bgRotate, ...rotate}, {...current.bgFlip, ...flip});
      editBgOption("custom"); current.editBgBlurValue(0); current.editBgBrightness(100); current.editIsBgAnimation(false);
      applied.current = key; setError("");
    }).catch(cause => { if (!stopped) setError(cause.message); });
    return () => { stopped = true; };
  }, [id, background, ready, key, dataChannel, locked, editBgOption, derived]);
  return error;
}
